'use client'
import { useEffect, useRef, useState } from 'react'
import { useVideoVisibilityContext } from '@/context/base/VideoVisibilityContext'

interface UseVideoVisibilityOptions {
  autoPlay?: boolean
  priority?: number
}

interface UseVideoVisibilityReturn {
  videoRef: React.RefObject<HTMLVideoElement>
  isVisible: boolean
  play: () => Promise<void>
  pause: () => void
  isPlaying: boolean
}

export const useVideoVisibility = (videoId: string, options: UseVideoVisibilityOptions = {}): UseVideoVisibilityReturn => {
  const { autoPlay = true, priority = 1 } = options
  const { registerVideo, unregisterVideo, visibleVideos } = useVideoVisibilityContext()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const isVisible = visibleVideos.has(videoId)

  // Register/unregister video with the intersection observer
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Register the video with the pooled observer
    const timer = setTimeout(() => {
      registerVideo(videoId, video)
    }, 100) // Slight delay to ensure video element is ready

    // Setup video event listeners
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleEnded = () => setIsPlaying(false)

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('ended', handleEnded)

    // Cleanup function
    return () => {
      clearTimeout(timer)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('ended', handleEnded)
      unregisterVideo(videoId, video)
    }
  }, [videoId, registerVideo, unregisterVideo])

  // Handle auto-play based on visibility
  useEffect(() => {
    const video = videoRef.current
    if (!video) console.log(`❌ Video ${videoId}: No video element found`)
    if (!video || !autoPlay) return

    if (isVisible) {
      // Play video when it comes into view
      // Higher priority videos (lower number) play immediately
      const delay = priority > 2 ? 100 : 0

      const timer = setTimeout(() => {
        video.play().catch((error) => {
          // Handle autoplay restrictions gracefully
          console.warn(`Video ${videoId} autoplay failed:`, error)
        })
      }, delay)

      return () => clearTimeout(timer)
    } else {
      // Pause video when it goes out of view
      video.pause()
    }
  }, [isVisible, autoPlay, priority, videoId])

  // Manual play function
  const play = async (): Promise<void> => {
    const video = videoRef.current
    if (!video) return

    try {
      await video.play()
    } catch (error) {
      console.warn(`Manual play failed for video ${videoId}:`, error)
      throw error
    }
  }

  // Manual pause function
  const pause = (): void => {
    const video = videoRef.current
    if (!video) return

    video.pause()
  }

  return {
    videoRef,
    isVisible,
    play,
    pause,
    isPlaying
  }
}
